import { Star, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Badge } from "@/components/ui/badge";

export const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const testimonials = [
    {
      name: "Ana Martinez",
      title: "Senior Product Designer",
      company: "Figma",
      avatar: "AM",
      quote: "youXforma cut our handoff time by 80%. It's an absolute game changer for our design-to-development workflow.",
      rating: 5,
    },
    {
      name: "David Chen",
      title: "Freelance Designer",
      company: "Independent",
      avatar: "DC",
      quote: "Best $15/month I spend. Literally pays for itself in the first project. My clients love the professional docs.",
      rating: 5,
    },
    {
      name: "Sarah Johnson",
      title: "Design Lead",
      company: "Stripe",
      avatar: "SJ",
      quote: "Our entire design team uses it daily. Can't imagine going back to manual documentation. Pure time savings.",
      rating: 5,
    },
    {
      name: "Marcus Kim",
      title: "UX Designer",
      company: "Shopify",
      avatar: "MK",
      quote: "The AI is surprisingly accurate. It catches details I would have missed. Developers actually thank me now.",
      rating: 5,
    },
    {
      name: "Elena Rodriguez",
      title: "Product Designer",
      company: "Notion",
      avatar: "ER",
      quote: "Finally, a tool that understands design systems. The component documentation is exactly what devs need.",
      rating: 5,
    },
    {
      name: "James Wilson",
      title: "Creative Director",
      company: "Airbnb",
      avatar: "JW",
      quote: "We've scaled from 2 designers to 12 and youXforma keeps our documentation consistent across all projects.",
      rating: 5,
    },
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background via-muted/10 to-background" ref={ref}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="mb-4 glass-card">
            <Star className="mr-2 h-3 w-3 fill-primary text-primary" />
            Testimonials
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Loved by <span className="gradient-text">Designers Worldwide</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join hundreds of designers who automated their documentation workflow
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="relative group"
            >
              <div className="glass-card p-6 hover-glow hover-lift h-full flex flex-col">
                {/* Quote Icon */}
                <Quote className="h-8 w-8 text-primary/20 mb-4" />

                {/* Rating */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-foreground mb-6 leading-relaxed flex-grow text-sm">
                  "{testimonial.quote}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-border">
                  <div className="w-12 h-12 rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center text-primary font-bold">
                    {testimonial.avatar}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground text-sm">{testimonial.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {testimonial.title}
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      @ {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative gradient on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};